import React from 'react';
import { useLocation } from 'react-router-dom';
import { getAuthMethod, getUser } from '@/utils/authSession';
import { linkTabs } from '../../tabHref';
import { registersTabsFor } from './registersTabsMeta';

/**
 * Registers shell state: which tab the URL selects, which tabs this session may
 * see, and nothing else.
 *
 * As thin as `useConfigurationsPage`, and for the same reason. Books owns a
 * date range because all four of its tabs report over a window; a register is
 * a list of things that exist now — assets, employees, shareholders — and the
 * runs that post them carry their own period. A shell-level window would have
 * nothing to scope.
 *
 * The one thing it does decide is **visibility**: Salaries is OrgAdmin-only on
 * the API, reads included, so advertising it to a BranchAdmin would be offering
 * a screen that can only 403. `registersTabsFor` owns that rule and its one
 * subtlety — see its note.
 */
export const useRegistersPage = () => {
    const { pathname } = useLocation();

    // Read once per mount, like `useSettingsPage`: the session cannot change
    // without a navigation that remounts this shell.
    const session = React.useMemo(
        () => ({ role: getUser()?.role ?? null, authMethod: getAuthMethod() }),
        [],
    );

    const { tabs: visibleTabs, selectedTabId } = React.useMemo(
        () => registersTabsFor(pathname, session.role, session.authMethod),
        [pathname, session],
    );

    // No shell-level range to carry, so the href is the tab's own path — see
    // `linkTabs`. Applied after the role filter, not before: a tab this session
    // may not see should not get a link either.
    const tabs = React.useMemo(() => linkTabs(visibleTabs), [visibleTabs]);

    return {
        tabs,
        selectedTabId,
    };
};
